import { map, catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import {
  Product,
  Price,
  NegotiationsService,
  ProductsService,
  NegotiationRequest,
  NegotiationEventRequest,
  ProductReservation,
  NegotiationListResponse,
  Negotiation
} from '@vestiaire/api-sdk-angular';
import { Observable, throwError, Subject } from 'rxjs';
import { ReservationService } from '@app/shared/services/product';

@Injectable()
export class MmaoService {
  static MAX_DISCOUNT = 0.7;
  public hideNotifications = new Subject<boolean>();

  constructor(
    private negotiationService: NegotiationsService,
    private productService: ProductsService,
    private reservationService: ReservationService
  ) {}

  /**
   * Returns the minimum price for an offer based on current price.
   *
   * @param currentPrice the starting negotiation price.
   *
   * @returns the minimum allowed offer, in whole number
   */
  getMinOffer(currentPrice: Price): number {
    if (currentPrice.cents) {
      return Math.ceil((currentPrice.cents * MmaoService.MAX_DISCOUNT) / 100);
    }

    return 0;
  }

  /**
   * Returns the maxium price for an offer based on current price.
   *
   * @param currentPrice the starting negotiation price.
   *
   * @returns the maximum allowed offer, in whole number
   */
  getMaxOffer(currentPrice: Price): number {
    if (currentPrice.cents) {
      return Math.round(currentPrice.cents / 100);
    }

    return 0;
  }

  getNegotiation(id: string): Observable<Negotiation> {
    return this.negotiationService
      .getNegotiation(id)
      .pipe(map(result => result.data))
      .pipe(catchError(error => throwError(error)));
  }

  getActiveBuyerNegotiation(productId: string, withEvents = false): Observable<Negotiation> {
    return this.productService
      .getActiveNegotiation(productId, withEvents)
      .pipe(map(result => result.data))
      .pipe(catchError(error => throwError(error)));
  }

  createNegotiation(product: Product, price: number, buyerId?: string): Observable<Negotiation> {
    const request: NegotiationRequest = {
      offerInWholeNumber: price
    };

    if (buyerId) {
      request.buyerId = buyerId;
    }

    return this.productService
      .createNegotiation(product.id, request)
      .pipe(map(result => result.data))
      .pipe(catchError(error => throwError(error)));
  }

  sendOffer(negotiation: Negotiation, price: number): Observable<Negotiation> {
    return this.addEvent(negotiation.id, 'offer', price);
  }

  acceptOffer(negotiation: Negotiation): Observable<Negotiation> {
    return this.addEvent(negotiation.id, 'accept');
  }

  rejectOffer(negotiation: Negotiation): Observable<Negotiation> {
    return this.addEvent(negotiation.id, 'reject');
  }

  private addEvent(
    negotiationId: string,
    action: NegotiationEventRequest.ActionEnum,
    price?: number
  ): Observable<Negotiation> {
    const event: NegotiationEventRequest = {
      action: action
    };

    if (action === 'offer') {
      event.offerInWholeNumber = price;
    }

    return this.negotiationService
      .addNegotiationEvent(negotiationId, event)
      .pipe(map(result => result.data))
      .pipe(catchError(error => throwError(error)));
  }

  /**
   * Here are the rules for product that are available to negotiations.
   *   - product MUST be on sale,
   *   - product MUST NOT be reserved by a premium member,
   *   - product MUST NOT be in a process of purchase,
   *   - product MUST NOT be in a fraud screening process,
   *   - product MUST NOT be prohibited for shipping,
   *   - product MUST NOT already be in a negotiation process by buyer,
   *   - seller MUST have enabled negotiation option for this product.
   */
  canMakeAnOffer(product: Product, reservation: ProductReservation, negotiation: Negotiation) {
    return this.mmaoCheck(product, reservation) && (!negotiation || !this.hasPendingNegotiations(negotiation));
  }

  sellerCanMakeAnOffer(product: Product, reservation: ProductReservation) {
    return this.mmaoCheck(product, reservation);
  }

  mmaoCheck(product: Product, reservation: ProductReservation) {
    return (
      !product.sold &&
      product.negotiable &&
      !(reservation && this.reservationService.isValid(reservation) && reservation.premium)
    );
  }

  hasPendingNegotiations(negotiation: Negotiation): boolean {
    if (!negotiation) {
      return false;
    }
    const expirationDate = new Date(negotiation.expirationDate);
    return !negotiation.isClosed && expirationDate > new Date();
  }

  getNegotiationsByProduct(idProduct: string): Observable<Negotiation[]> {
    return this.negotiationService.listNegotiations('seller', true).pipe(
      map((res: NegotiationListResponse) => res.data),
      map(negotiations => negotiations.filter(n => n.product.id === idProduct && !n.isClosed)),
      catchError(err => throwError(err))
    );
  }
}
